import { useState } from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  MapPin,
  ScanBarcode,
  Tag,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  narrowDirection,
  planLinesView,
  type LineChip,
  type LinesViewLine,
  type RenderedFact,
} from '../../../lib/lineLayout';
import { CellValue } from './CellValue';

/**
 * How many lines show before the expander appears.
 *
 * 43% of picklists have exactly one item and 73% have three or fewer, so on nearly half the feed this
 * threshold means the expander never renders at all — which is the point. It is a *presentation* cap; the
 * server sends every line and `+N more` counts from the FINISHED lines (bin-grouped where §5 grouped
 * them), never from a server-supplied total that could disagree with what is in hand.
 */
const COLLAPSED_ROW_COUNT = 3;

/**
 * How many identity chips a line shows before its own `+N` chip appears (owner ruling 2026-08-20).
 *
 * Measured: one bin reaches 20 serials while the average is 3.86 — so most lines show every serial with
 * no expander at all, and only the outliers collapse. The line expander above caps LINES; without this
 * cap a single 20-serial bin line would out-grow the whole block.
 */
const CHIP_CAP = 6;

/** Icon key → glyph. The key comes from `lineLayout`, which never sees a component.
 *  `ScanBarcode`, not `Barcode` — the plain barcode was ruled unsuitable in the design session (§7). */
const FACT_ICONS: Record<string, LucideIcon> = {
  bin: MapPin,
  serial: ScanBarcode,
  lot: Tag,
  from: ArrowLeft,
  to: ArrowRight,
};

const CHIP_ICONS: Record<LineChip['iconKey'], LucideIcon> = {
  serial: ScanBarcode,
  lot: Tag,
};

interface LinesBlockProps {
  /** Display headings, already human-written server-side. Positionally aligned to each row. */
  columns?: string[];
  /** A matrix of pre-formatted strings. */
  rows?: (string | null)[][];
  /** `'in'` / `'out'` when the block's quantities move stock (§6); anything else renders neutral. */
  direction?: string | null;
  className?: string;
}

/**
 * `shape: 'lines'` — the compact line list (design §1.4 "Line items").
 *
 * ⚠️ **THIS WAS A TABLE AND THE TABLE WAS WRONG.** It drew one column per declared field inside its own
 * horizontal scroller, on the reasoning that a wide breakdown must never push the page sideways. True, but
 * it solved the wrong problem: `PickRecorded` reached SIX columns when PW-09 added Bin and Serial, and six
 * columns do not fit 364px — so every reader met a scrollbar to find out which bin a pick came from.
 *
 * A list wraps where a table scrolls, and it still fits when a seventh column is declared.
 *
 * <b>Nothing here reads a payload key or an event type.</b> It reads the finished strings the server sent
 * and the headings it declared (§C1), which is what lets one renderer serve events added long after it.
 * Every layout decision — prose vs fact, bin-grouping, chips, direction — lives in `planLinesView` and its
 * helpers: pure, and therefore actually tested, which a component cannot be in this repo (no jsdom).
 */
export function LinesBlock({ columns, rows, direction, className }: LinesBlockProps) {
  const [expanded, setExpanded] = useState(false);
  // Per-LINE chip expansion — a Set so a 39-line block does not re-render the feed per toggle. Local
  // state, like the line expander: lifting it to the feed would re-render every row on each click.
  const [chipsOpen, setChipsOpen] = useState<ReadonlySet<number>>(new Set());

  // Defensive against a malformed payload rather than trusting the wire type: `EvidenceBlock` promises never
  // to throw, and a render-time `try/catch` in the parent could not honour that (children render later).
  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeRows = Array.isArray(rows) ? rows.filter(Array.isArray) : [];

  const view = planLinesView(safeColumns, safeRows, narrowDirection(direction));

  const hiddenCount = view.lines.length - COLLAPSED_ROW_COUNT;
  const visibleLines = expanded ? view.lines : view.lines.slice(0, COLLAPSED_ROW_COUNT);

  // A zero-row breakdown is REAL and meaningful — an auto-close with nothing left unpicked says something,
  // and rendering nothing would make it indistinguishable from a missing block (§C5).
  if (view.lines.length === 0) {
    return <p className={cn('text-xs text-muted-foreground', className)}>None</p>;
  }

  const toggleChips = (index: number) =>
    setChipsOpen((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    // ⚠️ There is NO global TooltipProvider in this app — a bare <Tooltip> throws and takes the page with
    // it. Every surface mounts its own; this is the panel's.
    <TooltipProvider delayDuration={200}>
      <div className={cn('text-xs', className)}>
        {/* Vertical capping only once expanded, so a 39-line pick cannot bury the feed. No horizontal
            scroller anywhere — that was the defect this replaced. */}
        <ul className={cn('space-y-1.5', expanded && 'max-h-56 overflow-y-auto pr-1')}>
          {visibleLines.map((line, lineIndex) => (
            <LineItem
              key={lineIndex}
              line={line}
              chipsExpanded={chipsOpen.has(lineIndex)}
              onToggleChips={() => toggleChips(lineIndex)}
            />
          ))}
        </ul>

        {hiddenCount > 0 && (
          <button
            type="button"
            aria-expanded={expanded}
            // ⚠️ stopPropagation is load-bearing: on the ADMIN feed this whole block renders inside
            // ActivityRow's row-wide onSelect <button>, so a bubbling click would ALSO open the detail
            // drawer — expanding a breakdown must never navigate.
            onClick={(event) => {
              event.stopPropagation();
              setExpanded((current) => !current);
            }}
            className={cn(
              'mt-[5px] rounded text-[11px] font-medium text-muted-foreground',
              'transition-colors hover:text-foreground motion-reduce:transition-none',
              // ⚠️ `ring-offset-background` is not optional beside `ring-offset-1`. Without it Tailwind
              // leaves --tw-ring-offset-color at its preflight default of #fff, so the focus ring draws a
              // hardcoded white halo — invisible in light, a bright halo on the dark panel, and a raw
              // colour that adapts to no theme. Every primitive in components/ui pairs the two.
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'focus-visible:ring-offset-1 ring-offset-background'
            )}
          >
            {/* Visible text, so this control needs no tooltip to be understood. */}
            {expanded ? 'Show less' : `+${hiddenCount} more`}
          </button>
        )}
      </div>
    </TooltipProvider>
  );
}

/**
 * One finished line. `leading-[1.4]`, `gap-x-2.5`, `gap-[3px]`, `mt-[3px]`, chip metrics etc. are
 * DELIBERATE arbitrary values — extracted from the prototype's inline styles (fidelity spec, 86d43a6v3
 * PART 3b–3d). Do not round them to the nearest scale step; the owner's bar is pixel fidelity.
 */
function LineItem({
  line,
  chipsExpanded,
  onToggleChips,
}: {
  line: LinesViewLine;
  chipsExpanded: boolean;
  onToggleChips: () => void;
}) {
  const visibleChips = chipsExpanded ? line.chips : line.chips.slice(0, CHIP_CAP);
  const ChipIcon = line.chips.length > 0 ? CHIP_ICONS[line.chips[0].iconKey] : null;

  return (
    <li className="leading-[1.4]">
      {!line.inline && (
        /* Code and description share ONE line and wrap together, capped at two lines. `min-w-0` lets
           the description shrink; `break-all` on the code because an item code is unbreakable text that
           would otherwise force the panel wider than its container. No `tabular-nums` on the code: the
           prototype styles it as plain mono. */
        <p className="line-clamp-2">
          <span className="font-mono font-semibold break-all text-foreground">
            <CellValue value={line.code} />
          </span>
          {line.description !== null && line.description !== '' && (
            <span className="ml-2 text-muted-foreground">
              <CellValue value={line.description} />
            </span>
          )}
        </p>
      )}

      {(line.inline || line.facts.length > 0) && (
        // flex-wrap is what makes this fit at any width: more facts means another line, never a
        // scrollbar.
        <div
          className={cn('flex flex-wrap items-center gap-x-2.5 gap-y-0.5', !line.inline && 'mt-0.5')}
        >
          {line.inline && (
            /* Bin-mode (§5, PART 3c): the bin code leads its own facts row — `📍 G-01-A-1  ↑ 2 PIECE`.
               `whitespace-nowrap` + `shrink-0` because a bin code must never break mid-code, and the
               real data holds 20-char codes with spaces (`BUNKER OIL MAIN TANK`). */
            <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap">
              <MapPin aria-hidden className="size-3 shrink-0 text-muted-foreground" />
              <span className="font-mono font-semibold text-foreground">
                <CellValue value={line.code} />
              </span>
            </span>
          )}
          {line.facts.map((fact) => (
            <FactSpan key={fact.label} fact={fact} />
          ))}
        </div>
      )}

      {line.chips.length > 0 && ChipIcon && (
        /* The identity chips row (§5, PART 3d): ONE muted icon introduces the row — decided PER LINE
           (§3b), because one posting block mixes lot, serial and untracked lines — then one chip per
           scanned identity, wrapping freely beneath the line. */
        <div className="mt-[3px] flex flex-wrap items-center gap-1">
          <ChipIcon aria-hidden className="size-3 shrink-0 text-muted-foreground" />
          {visibleChips.map((chip, chipIndex) => (
            <span
              key={chipIndex}
              /* PART 3d verbatim: 18px box, 6px padding, 5px radius, mono 10.5px/500 with tabular
                 figures, nowrap so a serial never breaks mid-code. Two typefaces in one chip is
                 DELIBERATE: the identity stays mono so codes align; the expiry switches to the UI face
                 so it does not compete. The pick time (owner 2026-10-08) follows the expiry's treatment;
                 its short form drops seconds and AM/PM, so the full time is the chip's native title (one
                 per chip, so native, never a Radix tooltip — UI-GUIDELINES §8). */
              title={chip.atTitle}
              className={cn(
                'inline-flex h-[18px] items-center whitespace-nowrap rounded-[5px] border border-border bg-muted/55 px-1.5 font-mono text-[10.5px] font-medium tabular-nums',
                chip.atTitle && 'cursor-help'
              )}
            >
              {chip.text}
              {chip.exp !== null && (
                <span className="ml-[5px] font-sans text-[10px] font-medium text-muted-foreground">
                  exp {chip.exp}
                </span>
              )}
              {chip.at && (
                <span className="ml-[5px] font-sans text-[10px] font-medium text-muted-foreground">
                  · {chip.at}
                </span>
              )}
            </span>
          ))}
          {line.chips.length > CHIP_CAP && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-expanded={chipsExpanded}
                  aria-label={
                    chipsExpanded
                      ? 'Show fewer identities'
                      : `Show all ${line.chips.length} identities`
                  }
                  // Same rule as the lines expander above: on the admin feed this sits inside the
                  // row's onSelect button, and a bubbled click would open the drawer.
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleChips();
                  }}
                  className={cn(
                    'inline-flex h-[18px] items-center whitespace-nowrap rounded-[5px] border border-border',
                    'bg-muted/55 px-1.5 font-mono text-[10.5px] font-medium tabular-nums text-muted-foreground',
                    'transition-colors hover:text-foreground motion-reduce:transition-none',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    'focus-visible:ring-offset-1 ring-offset-background'
                  )}
                >
                  {chipsExpanded ? 'less' : `+${line.chips.length - CHIP_CAP}`}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {chipsExpanded ? 'Show fewer identities' : `Show all ${line.chips.length} identities`}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </li>
  );
}

/** One fact: five states, one per branch — direction → wrapping → icon → no label → visible label. Every
 *  decision arrives on the `RenderedFact`; this component only draws. */
function FactSpan({ fact }: { fact: RenderedFact }) {
  // §6 — a directional quantity is ONE visual unit: arrow and value share the same status token at
  // weight 600. Green ↑ = stock in (the IR), red ↓ = stock out (the MIS). Only the quantity fact ever
  // carries a direction; lineLayout enforces that.
  if (fact.direction) {
    const DirectionIcon = fact.direction === 'in' ? ArrowUp : ArrowDown;
    const directionClass = fact.direction === 'in' ? 'text-success' : 'text-destructive';
    const directionName = fact.direction === 'in' ? 'Qty added' : 'Qty issued';
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center gap-[3px] whitespace-nowrap font-semibold',
              directionClass
            )}
            aria-label={`${directionName}: ${fact.value}`}
          >
            <DirectionIcon aria-hidden className="size-3 shrink-0" />
            <span className="tabular-nums">
              <CellValue value={fact.value} />
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{directionName}</TooltipContent>
      </Tooltip>
    );
  }

  // PT-7 A12 — a long labelled value ("For MRS" with its remarks and end use) wraps INSIDE the row instead of
  // running one nowrap line past the 364px dock, whose feed is `overflow-x-hidden` (ActivityDock asks any child
  // that can pass 364px for `min-w-0` plus its own breaking rule). The facts row is `flex flex-wrap`, so the
  // fact first moves to a line of its own, then wraps within it. The label stays on one line beside the
  // value's first line. Clamped at THREE lines by CSS only — nothing shortens the string — and ALWAYS titled
  // "{label}: {value}" (UI-GUIDELINES §8). The native title stands in for the heading tooltip, which this
  // branch does not mount, so a hover shows one popup, not two.
  // Why three (PT-7 A12 measurement, 2026-09-30, local copy): the p90 label is "{13-char MRS} — {61-char
  // remark, PT-2 M-2.4 p90} · {21-char end use, p90 of 150 lines}" ≈ 101 characters, and a dock row leaves
  // the value about 255px (~34-43 characters a line), so two lines cannot hold it. Past three, the title does.
  if (fact.wraps) {
    return (
      <span
        className="inline-flex min-w-0 max-w-full items-baseline gap-[3px] text-foreground"
        aria-label={`${fact.label}: ${fact.value}`}
        title={`${fact.label}: ${fact.value}`}
      >
        <span className="shrink-0 whitespace-nowrap text-[9.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground/70">
          {fact.label}
        </span>
        <span className="line-clamp-3 min-w-0 break-words">
          <CellValue value={fact.value} />
        </span>
      </span>
    );
  }

  const Icon = fact.iconKey ? FACT_ICONS[fact.iconKey] : null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* ⚠️ The heading is the accessible name whether or not it is drawn (UI gate rule 5). A hidden
            or icon-replaced label is unreadable to a new user, a keyboard user and a screen reader — so
            it is hidden visually, never discarded. `whitespace-nowrap` keeps an icon (or a direction
            arrow) glued to its value: a break between them changes the meaning. */}
        <span
          className="inline-flex items-center gap-[3px] whitespace-nowrap text-foreground"
          aria-label={`${fact.label}: ${fact.value}`}
        >
          {/* `hideLabel` is decided in lineLayout at merge time — the component only obeys. */}
          {Icon ? (
            <Icon aria-hidden className="size-3 shrink-0 text-muted-foreground" />
          ) : fact.hideLabel ? null : (
            <span className="text-[9.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground/70">
              {fact.label}
            </span>
          )}
          <span className="tabular-nums">
            <CellValue value={fact.value} />
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{fact.label}</TooltipContent>
    </Tooltip>
  );
}
